import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Activity, DollarSign, Cpu, HardDrive, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { useMonitoring } from '../context/MonitoringContext';

import { parseUtc } from '../lib/parseUtc';
const RealTimeMetrics = ({ clusterId }) => {
  const [historicalData, setHistoricalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  
  // Try to use context if available, fallback to local state
  let contextData = null;
  try {
    contextData = useMonitoring();
  } catch (e) {
    // Context not available, will use local fetch
  }

  const [localRealTimeData, setLocalRealTimeData] = useState(null);
  const realTimeData = contextData?.realTimeData || localRealTimeData;

  useEffect(() => {
    if (clusterId && !contextData) {
      fetchRealTimeMetrics();
      intervalRef.current = setInterval(fetchRealTimeMetrics, 30000);
    } else if (contextData) {
      setLoading(false);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [clusterId, contextData]);

  // Update historical data when realTimeData changes
  useEffect(() => {
    if (realTimeData) {
      setHistoricalData(prev => {
        const updated = [...prev, {
          timestamp: parseUtc(realTimeData.timestamp || Date.now()).toLocaleTimeString(),
          cost_per_hour: realTimeData.cost_per_hour || contextData?.costPerHour || 0,
          efficiency: realTimeData.efficiency_metrics?.overall_efficiency || contextData?.efficiency?.overall_efficiency || 0
        }];
        return updated.slice(-20);
      });
      setLoading(false);
    }
  }, [realTimeData, contextData]);

  const fetchRealTimeMetrics = async () => {
    try {
      const response = await axios.get(`${API}/realtime-metrics/${clusterId}`);
      setLocalRealTimeData(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch real-time metrics');
    } finally {
      setLoading(false);
    }
  };

  // Use context values for consistency
  const costPerSecond = contextData?.costPerSecond || realTimeData?.cost_per_second || 0;
  const costPerHour = contextData?.costPerHour || realTimeData?.cost_per_hour || 0;
  const podsCount = contextData?.podsCount || realTimeData?.pods_count || 0;
  const nodesCount = contextData?.nodesCount || realTimeData?.nodes_count || 0;
  const efficiency = contextData?.efficiency || realTimeData?.efficiency_metrics || {};

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount);
  };

  const getEfficiencyColor = (efficiency) => {
    if (efficiency >= 80) return 'text-green-600';
    if (efficiency >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getAlertSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'info': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="pb-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-full"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200">
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!realTimeData) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-gray-500">No real-time data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Real-time Cost Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark rounded-lg p-5 shadow-sm hover:shadow transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Cost per Second</p>
            <div className="p-2 bg-blue-50 dark:bg-blue-500/10 rounded-md">
              <DollarSign className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">{formatCurrency(costPerSecond)}</div>
          <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">Live cost rate</p>
        </div>

        <div className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark rounded-lg p-5 shadow-sm hover:shadow transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Cost per Hour</p>
            <div className="p-2 bg-emerald-50 dark:bg-emerald-500/10 rounded-md">
              <Activity className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
          <div className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">{formatCurrency(costPerHour)}</div>
          <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">Current hourly rate</p>
        </div>

        <div className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark rounded-lg p-5 shadow-sm hover:shadow transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Resource Utilization</p>
            <div className="p-2 bg-violet-50 dark:bg-violet-500/10 rounded-md">
              <TrendingUp className="w-4 h-4 text-violet-600 dark:text-violet-400" />
            </div>
          </div>
          <div className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">
            {(efficiency.overall_efficiency || 0).toFixed(1)}<span className="text-base text-slate-400 dark:text-slate-500 font-normal ml-0.5">%</span>
          </div>
          <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">CPU + memory in use right now</p>
        </div>

        <div className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark rounded-lg p-5 shadow-sm hover:shadow transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Active Resources</p>
            <div className="p-2 bg-amber-50 dark:bg-amber-500/10 rounded-md">
              <Cpu className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
          </div>
          <div className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">{podsCount}</div>
          <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">{nodesCount} nodes</p>
        </div>
      </div>

      {/* Real-time Cost Chart */}
      {historicalData.length > 1 && (
        <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
          <CardHeader className="border-b border-slate-100 dark:border-surface-border pb-3">
            <CardTitle className="text-sm font-semibold text-slate-700 dark:text-white">Real-time Cost Trend</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={historicalData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="timestamp" />
                  <YAxis />
                  <Tooltip
                    formatter={(value, name) => [
                      name === 'cost_per_hour' ? formatCurrency(value) : `${value.toFixed(1)}%`,
                      name === 'cost_per_hour' ? 'Cost/Hour' : 'Resource Utilization'
                    ]}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="cost_per_hour" 
                    stroke="#8884d8" 
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="efficiency" 
                    stroke="#82ca9d" 
                    strokeWidth={2}
                    dot={false}
                    yAxisId="right"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resource Utilization Breakdown */}
      {efficiency && (efficiency.cpu_efficiency || efficiency.memory_efficiency) && (
        <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
          <CardHeader className="border-b border-slate-100 dark:border-surface-border pb-3">
            <CardTitle className="text-sm font-semibold text-slate-700 dark:text-white">Resource Utilization Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium dark:text-text-secondary">CPU Efficiency</span>
                  <span className={`text-sm font-bold ${getEfficiencyColor(efficiency.cpu_efficiency || 0)}`}>
                    {(efficiency.cpu_efficiency || 0).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-surface-border rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{ width: `${Math.min(efficiency.cpu_efficiency || 0, 100)}%` }}
                  ></div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium dark:text-text-secondary">Memory Efficiency</span>
                  <span className={`text-sm font-bold ${getEfficiencyColor(efficiency.memory_efficiency || 0)}`}>
                    {(efficiency.memory_efficiency || 0).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-surface-border rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full" 
                    style={{ width: `${Math.min(efficiency.memory_efficiency || 0, 100)}%` }}
                  ></div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium dark:text-text-secondary">Overall Score</span>
                  <span className={`text-sm font-bold ${getEfficiencyColor(efficiency.overall_efficiency || 0)}`}>
                    {(efficiency.overall_efficiency || 0).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-surface-border rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full" 
                    style={{ width: `${Math.min(efficiency.overall_efficiency || 0, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Alerts */}
      {realTimeData.alerts && realTimeData.alerts.length > 0 && (
        <Card className="dark:bg-surface-dark dark:border-surface-border">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 dark:text-white">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              <span>Active Alerts</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {realTimeData.alerts.map((alert, index) => (
                <div key={index} className={`p-3 rounded-lg border ${getAlertSeverityColor(alert.severity)}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium">{alert.title}</h4>
                      <p className="text-sm mt-1">{alert.message}</p>
                    </div>
                    <Badge variant="outline" className={getAlertSeverityColor(alert.severity)}>
                      {alert.severity}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Last Updated */}
      <div className="text-xs text-gray-500 dark:text-text-secondary text-center">
        Last updated: {parseUtc(realTimeData.timestamp).toLocaleString()}
      </div>
    </div>
  );
};

export default RealTimeMetrics;